<script setup lang="ts">
  defineOptions({ name: 'CPSkeleton', inheritAttrs: false })

  // External library imports
  import type { PropType } from 'vue'

  import { HLSkeleton } from '@platform-ui/highrise'

  // Type imports

  // Props definitions
  const props = defineProps({
    // Skeleton type
    type: {
      type: String as PropType<'text' | 'avatar' | 'button' | 'image' | 'card'>,
      required: false,
      default: 'text',
    },
    // Skeleton size
    size: {
      type: String as PropType<'xs' | 'sm' | 'md' | 'lg' | 'xl'>,
      required: false,
      default: 'md',
    },
    // Number of lines for text skeleton
    repeat: {
      type: Number,
      required: false,
      default: 1,
    },
    // Width of the skeleton
    width: {
      type: [String, Number],
      required: false,
      default: undefined,
    },
    // Height of the skeleton
    height: {
      type: [String, Number],
      required: false,
      default: undefined,
    },
    // Whether the skeleton is animated
    animated: {
      type: Boolean,
      required: false,
      default: true,
    },
    // Whether the skeleton is rounded
    round: {
      type: Boolean,
      required: false,
      default: false,
    },
    id: {
      type: String,
      required: false,
      default: 'skeleton-container',
    },
  })
</script>

<template>
  <HLSkeleton
    :id="props.id"
    v-bind="$attrs"
    :type="props.type"
    :size="props.size"
    :repeat="props.repeat"
    :width="props.width"
    :height="props.height"
    :animated="props.animated"
    :round="props.round"
  />
</template>

<!--
  HighRise skeleton overrides — unscoped because HLSkeleton injects
  --n-color-start (#eee) and --n-color-end (#ddd) as INLINE styles on the
  .hr-skeleton element, so the shimmer is always light-gray regardless of the
  active theme. We override both variables (must use !important to beat the
  inline declarations) with surface tokens so the shimmer adapts to the brand
  background. NOTE: the variable is `--n-color-start`, NOT `--n-color` —
  HighRise's gradient reads from the *-start/-end pair and ignores --n-color.
-->
<style>
  .hr-skeleton {
    --n-color-start: var(--cp-surface-500) !important;
    --n-color-end: var(--cp-surface-600) !important;
  }
</style>
